import i18n from '../assets/i18n/config';

type WindowTarget = '_blank' | '_self' | '_parent' | '_top';

type ErrorCallback = (message: string) => void;

type SafeWindowOpenOptions = {
  target?: WindowTarget;
  onError?: ErrorCallback;
  trustedDomains?: readonly string[];
};

// OWASP recommendation: Limit URL length to prevent DoS attacks
const MAX_URL_LENGTH = 2048; // Common browser limit

/**
 * List of trusted domains for external redirects
 * Add your trusted S3 buckets, CDN domains, or partner sites here
 */
const TRUSTED_DOMAINS: readonly string[] = [
  // Add trusted domains here if needed, e.g.:
  // 's3.amazonaws.com',
  // 'your-bucket.s3.amazonaws.com',
];

const BLOCKED_PROTOCOLS: readonly string[] = [
  'javascript:',
  'data:text/html', // Block HTML data URLs as they can execute scripts
  'vbscript:',
  'file:',
  'about:',
] as const;

const LOCALHOST_HOSTNAMES: readonly string[] = [
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '[::1]',
] as const;

const DEFAULT_ERROR_MESSAGES = {
  INVALID_URL:
    'Invalid or unsafe URL. The link could not be opened for security reasons.',
  OPEN_FAILED: 'Failed to open the link. Please try again.',
} as const;

const handleSecurityError = (
  logMessage: string,
  logDetails: Record<string, unknown>,
  onError: ErrorCallback | undefined,
): void => {
  logSecurityError(logMessage, logDetails);

  onError?.(i18n.t('shared:unsafeUrl') || DEFAULT_ERROR_MESSAGES.INVALID_URL);
};

const logSecurityWarning = (message: string, details?: unknown): void => {
  console.warn(`[Security] ${message}`, details || '');
};

const logSecurityError = (message: string, details?: unknown): void => {
  console.error(`[Security] ${message}`, details || '');
};

const toUrlString = (url: string | URL): string => {
  return url instanceof URL ? url.href : url;
};

const isLocalhost = (hostname: string): boolean => {
  return LOCALHOST_HOSTNAMES.includes(hostname);
};

const hasBlockedProtocol = (url: string | URL): boolean => {
  // Use string matching instead of URL.protocol for early detection
  // - Handles relative URLs (e.g., '/path') without parsing
  // - Defense in depth: catches malicious protocols before URL parsing
  const urlString = toUrlString(url);
  const lowerUrl = urlString.toLowerCase();

  for (const blocked of BLOCKED_PROTOCOLS) {
    if (lowerUrl.startsWith(blocked)) {
      logSecurityWarning(
        `Blocked dangerous URL protocol: ${blocked}`,
        urlString,
      );

      return true;
    }
  }

  return false;
};

const isSafeDataUrl = (url: string): boolean => {
  // Only allow specific, safe MIME types with base64 encoding
  // Prevents XSS via data URLs with executable content
  const safeDataUrlPattern =
    /^data:(application\/pdf|image\/(png|jpeg|jpg|gif|webp|svg\+xml)|application\/vnd\.(openxmlformats-officedocument[\w.-]*|ms-excel)|text\/csv);base64,/i;
  const isAllowed = safeDataUrlPattern.test(url);

  if (!isAllowed) {
    logSecurityWarning('Blocked unsafe data URL:', url.substring(0, 100));
  }

  return isAllowed;
};

/**
 * Validates that a blob URL is safe (same-origin check)
 *
 * Blob URLs are safe when they originate from the same origin as the current page
 * because they can only reference data created by the current document.
 *
 * Security considerations:
 * - Blob URLs inherit the origin of the document that created them
 * - Cross-origin blob URLs cannot be accessed due to browser security
 * - Same-origin blob URLs are safe as they reference locally-created data
 * - HTTPS is enforced for blob origins (except localhost) for defense in depth
 *
 * @param url - The blob URL to validate
 * @param trustedDomains - Optional array of trusted domains to allow blob URLs from
 * @returns true if the blob URL is same-origin or from a trusted domain
 */
const isSafeBlobUrl = (
  url: string,
  trustedDomains?: readonly string[],
): boolean => {
  try {
    // blob: URLs have the format: blob:https://example.com/uuid
    const parsedUrl = new URL(url);
    const blobOrigin = parsedUrl.origin;

    // Handle invalid blob URLs that result in "null" origin
    if (blobOrigin === 'null') {
      logSecurityWarning('Blocked blob URL with null origin:', url);

      return false;
    }

    const currentOrigin = globalThis.location.origin;

    // Same-origin blob URLs are always safe
    if (blobOrigin === currentOrigin) {
      return true;
    }

    const blobOriginUrl = new URL(blobOrigin);

    // Enforce HTTPS for blob origins (except localhost) - defense in depth
    if (
      blobOriginUrl.protocol === 'http:' &&
      !isLocalhost(blobOriginUrl.hostname)
    ) {
      logSecurityWarning('Blocked blob URL with non-HTTPS origin:', blobOrigin);

      return false;
    }

    // Check if blob origin is from a trusted domain
    const allTrustedDomains = getMergedTrustedDomains(trustedDomains);

    if (
      allTrustedDomains.length > 0 &&
      isHostnameTrusted(blobOriginUrl.hostname, allTrustedDomains)
    ) {
      return true;
    }

    logSecurityWarning(
      `Blocked cross-origin blob URL. Current origin: ${currentOrigin}, blob origin: ${blobOrigin}`,
      url,
    );

    return false;
  } catch (e) {
    logSecurityWarning('Invalid blob URL format:', `${url} - ${e}`);

    return false;
  }
};

const isHostnameTrusted = (
  hostname: string,
  trustedDomains: readonly string[],
): boolean => {
  const lowerHostname = hostname.toLowerCase();

  return trustedDomains.some((domain) => {
    const lowerDomain = domain.toLowerCase();

    return (
      lowerHostname === lowerDomain || lowerHostname.endsWith(`.${lowerDomain}`)
    );
  });
};

const getMergedTrustedDomains = (
  trustedDomains?: readonly string[],
): readonly string[] => {
  return [...TRUSTED_DOMAINS, ...(trustedDomains || [])];
};

/**
 * Validates that a URL is safe to open (prevents open redirect vulnerabilities)
 *
 * This function implements OWASP best practices for preventing open redirects:
 * - Validates URL protocols (https/data for safe file types, http only for localhost)
 * - Checks for dangerous protocols (javascript:, file:, vbscript:, etc.)
 * - Allows relative URLs within the application
 * - Allows same-origin blob URLs (for locally-created file previews)
 * - Optionally validates against a trusted domain allowlist
 * - Prevents null byte injection attacks
 * - Validates URL length to prevent DoS attacks
 * - Enforces HTTPS for non-localhost domains
 *
 * @param url - The URL to validate (string or URL object)
 * @param trustedDomains - Optional array of additional trusted domains
 * @returns true if the URL is safe to open, false otherwise
 */
export const isSafeUrl = (
  url: string | URL | null | undefined,
  trustedDomains?: readonly string[],
): boolean => {
  if (!url) {
    return false;
  }

  const urlString = toUrlString(url);

  if (typeof urlString !== 'string') {
    return false;
  }

  const trimmedUrl = urlString.trim();

  if (!trimmedUrl) {
    return false;
  }

  if (trimmedUrl.length > MAX_URL_LENGTH) {
    logSecurityWarning(
      'URL exceeds maximum length:',
      `Length: ${trimmedUrl.length}`,
    );

    return false;
  }

  if (trimmedUrl.includes('\0') || trimmedUrl.includes('%00')) {
    logSecurityWarning('Blocked URL with null byte:', trimmedUrl);

    return false;
  }

  if (hasBlockedProtocol(url)) {
    return false;
  }

  try {
    if (trimmedUrl.startsWith('/')) {
      if (trimmedUrl.startsWith('//')) {
        logSecurityWarning('Blocked protocol-relative URL:', trimmedUrl);

        return false;
      }

      return true;
    }

    const parsedUrl = url instanceof URL ? url : new URL(trimmedUrl);

    if (parsedUrl.protocol === 'data:') {
      return isSafeDataUrl(trimmedUrl);
    }

    if (parsedUrl.protocol === 'blob:') {
      return isSafeBlobUrl(trimmedUrl, trustedDomains);
    }

    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      logSecurityWarning('Blocked non-HTTP(S) protocol:', parsedUrl.protocol);

      return false;
    }

    if (parsedUrl.protocol === 'http:' && !isLocalhost(parsedUrl.hostname)) {
      logSecurityWarning(
        'Blocked non-localhost HTTP URL (HTTPS required):',
        trimmedUrl,
      );

      return false;
    }

    const allTrustedDomains = getMergedTrustedDomains(trustedDomains);

    if (allTrustedDomains.length === 0) {
      return true; // Allow all HTTPS URLs when no trusted domains specified
    }

    const isTrusted = isHostnameTrusted(parsedUrl.hostname, allTrustedDomains);

    if (!isTrusted) {
      logSecurityWarning(
        `URL hostname not in trusted domains: ${parsedUrl.hostname}`,
        `URL: ${trimmedUrl}`,
      );
    }

    return isTrusted;
  } catch (e) {
    logSecurityWarning('Invalid URL format:', `${trimmedUrl} - ${e}`);

    return false;
  }
};

/**
 * @see /docs/URL_SECURITY_UTIL.md
 *
 * Safely opens a URL in a new window/tab after validation
 *
 * Implements OWASP recommendations for safe URL handling:
 * - Validates URL before opening
 * - Logs security events
 * - Provides user feedback on blocked attempts (via i18n translations)
 * - Sets secure window features (noopener, noreferrer)
 *
 * @param url - The URL to open (string or URL object)
 * @param options - Optional configuration object
 * @param options.target - The target for window.open (default: '_blank')
 * @param options.onError - Optional callback when URL is invalid (receives translated error message)
 * @param options.trustedDomains - Optional array of additional trusted domains
 * @returns true if the URL was opened, false if it was rejected
 *
 * @example
 * safeWindowOpen(url, {
 *   target: '_blank',
 *   onError: (msg) => enqueueSnackbar(msg, { variant: 'error' })
 * });
 */
export const safeWindowOpen = (
  url: string | URL | null | undefined,
  options?: SafeWindowOpenOptions,
): boolean => {
  const { target = '_blank', onError, trustedDomains } = options || {};

  if (!url) {
    return false;
  }

  const urlString = toUrlString(url);

  if (typeof urlString !== 'string') {
    handleSecurityError(
      'URL is not a valid string or URL object',
      { url },
      onError,
    );

    return false;
  }

  if (!isSafeUrl(url, trustedDomains)) {
    handleSecurityError(
      'Blocked unsafe redirect attempt',
      { url: urlString, userAgent: navigator.userAgent },
      onError,
    );

    return false;
  }

  const trimmedUrl = urlString.trim();

  try {
    window.open(trimmedUrl, target, 'noopener,noreferrer');

    return true;
  } catch (error: unknown) {
    const errorDetails = error instanceof Error ? error.message : String(error);

    logSecurityError('Failed to open URL:', {
      url: trimmedUrl,
      error: errorDetails,
    });

    onError?.(i18n.t('shared:unsafeUrl') || DEFAULT_ERROR_MESSAGES.OPEN_FAILED);

    return false;
  }
};
